import { share } from '../../connection';
import {
  type DocClock,
  type DocClocks,
  DocSyncStorageBase,
} from '../../storage';
import { IDBConnection, type IDBConnectionOptions } from './db';

type ClockField = 'clock' | 'pulledClock' | 'pushedClock';

export class IndexedDBDocSyncStorage extends DocSyncStorageBase {
  static readonly identifier = 'IndexedDBDocSyncStorage';

  constructor(private readonly options: IDBConnectionOptions) {
    super();
  }

  readonly connection = share(new IDBConnection(this.options));

  /** Sync metadata passes the same account/auth gate as doc payloads. */
  private get gate() {
    return this.connection.encryption;
  }

  override async getPeerRemoteClock(
    peer: string,
    docId: string
  ): Promise<DocClock | null> {
    const record = await this.gate.transaction(['peerClocks'], 'readonly', tx =>
      tx.objectStore('peerClocks').get([peer, docId])
    );

    return record
      ? {
          docId: record.docId,
          timestamp: record.clock,
        }
      : null;
  }
  override async getPeerRemoteClocks(peer: string) {
    const records = await this.gate.transaction(
      ['peerClocks'],
      'readonly',
      tx => tx.objectStore('peerClocks').index('peer').getAll(peer)
    );

    return records.reduce((clocks, { docId, clock }) => {
      clocks[docId] = clock;
      return clocks;
    }, {} as DocClocks);
  }

  override async setPeerRemoteClock(peer: string, clock: DocClock) {
    await this.advanceClock(peer, clock, 'clock');
  }

  /**
   * sl-oiya.4.2: Batch-write remote clocks in a single IDB transaction.
   * 1 transaction instead of N — dramatically faster for 100+ clocks.
   */
  override async setPeerRemoteClocks(
    peer: string,
    clocks: DocClock[]
  ): Promise<void> {
    if (clocks.length === 0) return;
    // A known, not newer clock costs a read, not a write transaction.
    const known = await this.gate.transaction(['peerClocks'], 'readonly', tx =>
      Promise.all(
        clocks.map(clock =>
          tx.objectStore('peerClocks').get([peer, clock.docId])
        )
      )
    );
    if (
      clocks.every((clock, i) => known[i] && known[i].clock >= clock.timestamp)
    )
      return;
    await this.gate.transaction(['peerClocks'], 'readwrite', async tx => {
      const store = tx.objectStore('peerClocks');
      for (const clock of clocks) {
        const record = await store.get([peer, clock.docId]);
        if (!record || record.clock < clock.timestamp) {
          await store.put({
            peer,
            docId: clock.docId,
            clock: clock.timestamp,
            pulledClock: record?.pulledClock ?? new Date(0),
            pushedClock: record?.pushedClock ?? new Date(0),
          });
        }
      }
    });
  }

  override async deletePeerDocClocks(
    peer: string,
    docId: string
  ): Promise<void> {
    await this.gate.transaction(['peerClocks'], 'readwrite', async tx => {
      const store = tx.objectStore('peerClocks');
      await store.delete([peer, docId]);
    });
  }

  override async getPeerPulledRemoteClock(
    peer: string,
    docId: string
  ): Promise<DocClock | null> {
    const record = await this.gate.transaction(['peerClocks'], 'readonly', tx =>
      tx.objectStore('peerClocks').get([peer, docId])
    );

    return record
      ? {
          docId: record.docId,
          timestamp: record.pulledClock,
        }
      : null;
  }
  override async getPeerPulledRemoteClocks(peer: string) {
    const records = await this.gate.transaction(
      ['peerClocks'],
      'readonly',
      tx => tx.objectStore('peerClocks').index('peer').getAll(peer)
    );

    return records.reduce((clocks, { docId, pulledClock }) => {
      clocks[docId] = pulledClock;
      return clocks;
    }, {} as DocClocks);
  }
  override async setPeerPulledRemoteClock(peer: string, clock: DocClock) {
    await this.advanceClock(peer, clock, 'pulledClock');
  }

  override async getPeerPushedClock(
    peer: string,
    docId: string
  ): Promise<DocClock | null> {
    const record = await this.gate.transaction(['peerClocks'], 'readonly', tx =>
      tx.objectStore('peerClocks').get([peer, docId])
    );

    return record
      ? {
          docId: record.docId,
          timestamp: record.pushedClock,
        }
      : null;
  }
  override async getPeerPushedClocks(peer: string) {
    const records = await this.gate.transaction(
      ['peerClocks'],
      'readonly',
      tx => tx.objectStore('peerClocks').index('peer').getAll(peer)
    );

    return records.reduce((clocks, { docId, pushedClock }) => {
      clocks[docId] = pushedClock;
      return clocks;
    }, {} as DocClocks);
  }

  override async setPeerPushedClock(peer: string, clock: DocClock) {
    await this.advanceClock(peer, clock, 'pushedClock');
  }

  /**
   * Moves one clock of a doc forward. A known, not newer clock costs a read,
   * not a write transaction; the write checks again, a newer clock may have
   * come in between.
   */
  private async advanceClock(peer: string, clock: DocClock, field: ClockField) {
    const known = await this.gate.transaction(['peerClocks'], 'readonly', tx =>
      tx.objectStore('peerClocks').get([peer, clock.docId])
    );
    if (known && known[field] >= clock.timestamp) return;
    await this.gate.transaction(['peerClocks'], 'readwrite', async tx => {
      const store = tx.objectStore('peerClocks');
      const record = await store.get([peer, clock.docId]);
      if (record && record[field] >= clock.timestamp) return;
      const next = {
        peer,
        docId: clock.docId,
        clock: record?.clock ?? new Date(0),
        pulledClock: record?.pulledClock ?? new Date(0),
        pushedClock: record?.pushedClock ?? new Date(0),
      };
      next[field] = clock.timestamp;
      await store.put(next);
    });
  }

  override async clearClocks() {
    await this.gate.transaction(['peerClocks'], 'readwrite', async tx => {
      const store = tx.objectStore('peerClocks');

      await store.clear();
    });
  }
}
